import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslations } from 'use-intl';
import { useLanguage, localizeQuestionHtml } from 'i18n';
import { Comments, Button } from 'components';
import { motion } from 'motion/react';
import { Markup } from 'interweave';
import { useWindowSize, usePresence } from 'hooks';
import { FirebaseContext, LocalContext } from 'context';
import { convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { Arrow } from 'assets/svgs';

// Preserve empty paragraphs by adding non-breaking space
const preserveEmptyParagraphs = (html) =>
  html.replace(/<p><\/p>/g, '<p>&nbsp;</p>');

// Remove trailing empty paragraphs from the end
const removeTrailingEmptyParagraphs = (html) =>
  html.replace(/(<p>(&nbsp;|\s*)<\/p>\s*)+$/g, '');

function SelectedQuestion({
  selectedQuestion,
  setSelectedQuestion,
  setQuestionIdQueryParam,
  users,
  sessionId
}) {
  const t = useTranslations('Review');
  const { language } = useLanguage();
  const { handleDialog } = useContext(LocalContext);
  const { firebase, user } = useContext(FirebaseContext);
  const { width } = useWindowSize();
  const [comments, setComments] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [postingOrEditingComment, setPostingOrEditingComment] = useState(false);
  const [commentBeingEdited, setCommentBeingEdited] = useState(null);
  const [idOfCommentBeingRepliedTo, setIdOfCommentBeingRepliedTo] =
    useState(null);
  const [deletingComment, setDeletingComment] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);

  // Initialize presence tracking
  usePresence({ firebase, user, sessionId });

  useEffect(() => {
    let unsubscribeToComments;
    if (firebase) {
      unsubscribeToComments = firebase.questions.subscribeToComments({
        sessionId,
        questionId: selectedQuestion.id,
        snapshot: (snapshot) => {
          const _comments = [];

          if (!snapshot.empty) {
            snapshot.forEach((doc) => {
              const data = doc.data();
              if (data.timestamp) {
                _comments.push({
                  id: doc.id,
                  ...data
                });
              }
            });
          }

          if (
            _comments.some((_comment) => _comment.uid === user?.uid) ||
            user?.isModerator?.includes(sessionId)
          ) {
            setComments(_comments);
          }
        }
      });
    }
    return () => {
      if (unsubscribeToComments) {
        unsubscribeToComments();
      }
    };
  }, [firebase]);

  // Subscribe to presence data
  useEffect(() => {
    let unsubscribeToPresence;
    if (firebase && sessionId) {
      unsubscribeToPresence = firebase.presence.subscribeToSessionPresence({
        sessionId,
        callback: (_onlineUsers) => {
          setOnlineUsers(_onlineUsers);
        }
      });
    }
    return () => {
      if (unsubscribeToPresence) {
        unsubscribeToPresence();
      }
    };
  }, [firebase, sessionId]);

  const handleMentions = ({
    html,
    parentCommentId,
    parentCommenterUID,
    commentURL
  }) => {
    const parser = new DOMParser();

    const doc = parser.parseFromString(html, 'text/html');

    const anchorElements = doc.querySelectorAll('a');

    let mentionedUIDs = Array.from(anchorElements).map((element) =>
      element.getAttribute('href')
    );

    if (parentCommentId) {
      mentionedUIDs = mentionedUIDs.filter((uid) => uid !== parentCommenterUID);
    }

    if (mentionedUIDs.length) {
      // Sender identity and the commenter's name are resolved server-side
      // (config/tenant + the caller's own Firestore record) — only the
      // comment link crosses the boundary. Requires the hardened email
      // functions (kol-exchange-backend `harden-email-functions`).
      firebase.emails.sendMentionNotifications({
        mentionedUIDs,
        sessionId,
        emailData: {
          TemplateModel: {
            comment_url: commentURL
          }
        }
      });
    }
  };

  const handlePostComment = async ({
    editorState,
    parentCommentId,
    parentCommenterUID
  }) => {
    try {
      setPostingOrEditingComment(true);
      const rawHtml = draftToHtml(
        convertToRaw(editorState.getCurrentContent())
      );
      const html = removeTrailingEmptyParagraphs(
        preserveEmptyParagraphs(rawHtml)
      );

      const questionId = selectedQuestion.id;

      const result = await firebase.questions.addComment({
        sessionId,
        questionId,
        user,
        html,
        parentCommentId: parentCommentId || null,
        language
      });

      if (result.id) {
        const commentId = result.id;

        const commentURL = `${window.location.href}?questionId=${questionId}&commentId=${commentId}`;

        if (parentCommentId) {
          // Names and sender identity are resolved server-side from Firestore
          // records — only the comment link crosses the boundary.
          firebase.emails.sendReplyNotification({
            parentCommenterUID,
            sessionId,
            emailData: {
              TemplateModel: {
                comment_url: commentURL
              }
            }
          });
        }

        handleMentions({
          html,
          parentCommentId,
          parentCommenterUID,
          commentURL
        });
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message);
      setPostingOrEditingComment(false);
    } finally {
      setPostingOrEditingComment(false);
    }
  };

  const handleEditComment = async ({
    editorState,
    commentBeingEdited: _commentBeingEdited,
    parentCommentId,
    parentCommenterUID
  }) => {
    try {
      setPostingOrEditingComment(true);
      const rawHtml = draftToHtml(
        convertToRaw(editorState.getCurrentContent())
      );
      const updatedHtml = removeTrailingEmptyParagraphs(
        preserveEmptyParagraphs(rawHtml)
      );
      const { id: commentId, questionId } = _commentBeingEdited;

      await firebase.questions.editComment({
        commentId,
        sessionId,
        questionId,
        html: updatedHtml
      });

      const commentURL = `${window.location.href}?questionId=${questionId}&commentId=${commentId}`;

      handleMentions({
        html: updatedHtml,
        parentCommentId,
        parentCommenterUID,
        commentURL
      });

      setCommentBeingEdited(null);
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message);
    } finally {
      setPostingOrEditingComment(false);
    }
  };

  const handleDeleteComment = async (commentToDelete) => {
    try {
      setDeletingComment(true);

      // Find all child comments recursively
      const getAllChildComments = (parentId) => {
        const children = comments.filter((c) => c.parentCommentId === parentId);
        let allChildren = [...children];

        children.forEach((child) => {
          allChildren = allChildren.concat(getAllChildComments(child.id));
        });

        return allChildren;
      };

      const childComments = getAllChildComments(commentToDelete.id);
      const allCommentsToDelete = [commentToDelete, ...childComments];

      // Delete children first, then parent (bottom-up)
      await Promise.all(
        allCommentsToDelete.reverse().map((comment) =>
          firebase.questions.deleteComment({
            sessionId,
            questionId: selectedQuestion.id,
            commentId: comment.id
          })
        )
      );
    } catch (error) {
      console.error('Error deleting comment:', error);
      setErrorMessage(t('deleteCommentError'));
    } finally {
      setDeletingComment(false);
    }
  };

  return (
    <Container id="selected-question">
      <QuestionContainer>
        <Button
          icon={<Arrow width={width < 768 ? '0.938rem' : '0.7325rem'} />}
          backgroundColor="var(--blue)"
          onClick={() => setSelectedQuestion(null)}
        />
        <h4>{t('questionNumber', { number: selectedQuestion.order })}</h4>
        <Markup
          content={localizeQuestionHtml(selectedQuestion, language)}
          tagName="div"
          className="markup"
        />
        <Note>{t('selectedNote')}</Note>
        <Divider $addBottomMargin={comments.length === 0} />
      </QuestionContainer>
      <Comments
        comments={comments}
        questionId={selectedQuestion.id}
        handleEditComment={handleEditComment}
        handlePostComment={handlePostComment}
        handleDeleteComment={handleDeleteComment}
        commentBeingEdited={commentBeingEdited}
        setCommentBeingEdited={setCommentBeingEdited}
        idOfCommentBeingRepliedTo={idOfCommentBeingRepliedTo}
        setIdOfCommentBeingRepliedTo={setIdOfCommentBeingRepliedTo}
        postingOrEditingComment={postingOrEditingComment}
        setQuestionIdQueryParam={setQuestionIdQueryParam}
        users={users}
        sessionId={sessionId}
        deletingComment={deletingComment}
        handleDialog={handleDialog}
        onlineUsers={onlineUsers}
      />
    </Container>
  );
}

const Container = styled(motion.section).attrs({
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  }
})`
  position: relative;
  margin-bottom: -40px;
  top: -6rem;

  @media (min-width: 1200px) {
    margin: 0;
    grid-column: 2/9;
  }
`;

const QuestionContainer = styled.div`
  background: #fff;
  border-top-left-radius: var(--border-radius-md);
  border-top-right-radius: var(--border-radius-md);
  display: grid;
  grid-column-gap: 1.25rem;
  grid-template-columns: repeat(4, 1fr);
  margin: 0 1.25rem;
  overflow-x: hidden;
  padding: 1.875rem 1.75rem 0;

  > * {
    grid-column: 1 / -1;
  }

  h4 {
    color: var(--blue);
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    margin-bottom: 1.15em;
    text-transform: uppercase;
  }

  /* Back button */
  > button {
    border-radius: 50%;
    height: 2rem;
    margin-bottom: 1.438rem;
    padding: initial;
    width: 2rem;
    z-index: 0;
  }

  .markup {
    color: var(--brown);
    font-family: 'Inter', sans-serif;
    font-size: 1.25rem;
    line-height: 120%;
    margin-bottom: 0.6em;

    p {
      margin: 0.75em 0 1em;
    }

    p:first-of-type {
      margin-top: 0;
    }

    img {
      margin-bottom: 1.8rem !important;
    }
  }

  @media (min-width: 768px) {
    padding: 1.875rem 3.7rem 0;

    h4 {
      grid-row: 1;
      align-self: center;
    }

    button {
      height: 1.5rem;
      width: 1.5rem;
      position: relative;
      left: -2.45rem;
      top: 1px;
    }

    button,
    .markup {
      grid-row: 2;
    }

    .markup {
      font-size: 1.5rem;
    }
  }

  @media (min-width: 1200px) {
    margin: 0;
  }
`;

const Note = styled.p`
  color: var(--blue);
  font-weight: 700;
  line-height: 120%;
  margin-bottom: 1.85rem;
`;

const Divider = styled.div`
  background-color: var(--light-grey);
  height: 0.063rem;
  left: -1.15rem;
  position: relative;
  width: calc(100% + 2.3rem);

  @media (min-width: 768px) {
    left: -0.35rem;
    width: calc(100% + 0.7rem);
  }
`;

export default SelectedQuestion;
